import { useState } from "react";
import { createFeedApi } from "../../app/api";
import Cookies from "js-cookie";
import { createPortal } from "react-dom";
import { convertImageToWebP } from "../../utils/convertImageToWebP";
import { ImagePlus, X, UploadCloud } from "lucide-react";

export default function CreatePostModal({ showModal, setShowModal, onPostCreated }) {

  const [postText, setPostText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!file.type?.startsWith("image/")) {
      alert("Please upload an image file.");
      return;
    }
    setImageFile(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    handleFileSelect(event.dataTransfer.files?.[0]);
  };

  const handleCreate = async () => {
    const token = Cookies.get("token");
    if (!token) {
      alert("You are not logged in!");
      return;
    }

    if (!postText.trim() && !imageFile) return;

    const formData = new FormData();
    formData.append("type", "post");
    formData.append("priority", 1);
    formData.append("meta", JSON.stringify({ message: postText }));

    if (imageFile) {
      formData.append("image", await convertImageToWebP(imageFile));
    }

    const res = await createFeedApi(formData, true);

    if (res.success) {
      setPostText("");
      setImageFile(null);
      setIsDragging(false);
      setShowModal(false);
    //   onPostCreated(); // reload feeds
    onPostCreated?.();
    }
  };

  if (!showModal) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/30 backdrop-blur-md flex items-center justify-center z-[9999]">

      <div className="relative w-[420px] rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-2xl p-6 text-white">

        <button
          onClick={() => setShowModal(false)}
          className="absolute top-3 right-4 rounded-full p-2 text-white/70 transition hover:bg-white/10 hover:text-red-200"
          aria-label="Close create post modal"
        >
          <X className="h-5 w-5" />
        </button>

        <h2 className="text-lg font-semibold mb-4 flex justify-center">
          Create Post
        </h2>

        <textarea
          placeholder="What's on your mind?"
          value={postText}
          onChange={(e) => setPostText(e.target.value)}
          className="w-full bg-white/10 border border-white/20 p-3 rounded-lg outline-none shadow-lg"
        />

        <label
          onDragEnter={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            setIsDragging(false);
          }}
          onDrop={handleDrop}
          className={`mt-4 flex min-h-[132px] cursor-pointer flex-col items-center justify-center rounded-2xl border border-dashed p-5 text-center transition ${
            isDragging
              ? "border-cyan-300 bg-cyan-300/15 shadow-[0_0_35px_rgba(34,211,238,0.22)]"
              : "border-white/25 bg-white/8 hover:border-cyan-300/70 hover:bg-white/12"
          }`}
        >
          <input
            type="file"
            accept="image/*"
            onChange={(e) => handleFileSelect(e.target.files?.[0])}
            className="sr-only"
          />

          {imageFile ? (
            <div className="flex w-full items-center gap-3 rounded-xl border border-white/10 bg-black/20 p-3 text-left">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-emerald-400/15 text-emerald-200">
                <ImagePlus className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-white">{imageFile.name}</p>
                <p className="text-xs text-white/45">
                  {(imageFile.size / 1024 / 1024).toFixed(2)} MB selected
                </p>
              </div>
              <button
                type="button"
                onClick={(event) => {
                  event.preventDefault();
                  setImageFile(null);
                }}
                className="rounded-lg p-2 text-white/45 transition hover:bg-white/10 hover:text-white"
                aria-label="Remove selected image"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ) : (
            <>
              <div className="mb-3 grid h-12 w-12 place-items-center rounded-2xl bg-cyan-300/12 text-cyan-200 ring-1 ring-cyan-300/20">
                <UploadCloud className="h-6 w-6" />
              </div>
              <p className="text-sm font-semibold text-white">
                Drag and drop image here
              </p>
              <p className="mt-1 text-xs text-white/45">
                or click to browse from your device
              </p>
            </>
          )}
        </label>

        <button
          onClick={handleCreate}
          className="mt-5 w-full bg-[#2C1A60] hover:bg-[#39227c] py-2 rounded-xl font-semibold"
        >
          Post
        </button>

      </div>

    </div>,
        document.body
  );
}
